import { useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { LineChart, Line, Area, AreaChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts'
import { parseISO } from 'date-fns'
import { formatUTCToLocal, formatChartTimestamp } from '@/utils/date'
import { useUserUnits, convertTemp, tempLabel } from '@/utils/units'
import { Droplet, Battery, TrendingUp, TrendingDown, Minus, ThermometerSun, CloudRain } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { deviceService } from '@/services/device.service'
import type { WaterSenseMeasurement } from '@/types/measurement.types'
import type { Device } from '@/types/device.types'

interface WatersenseViewProps {
  device: Device
  measurements: WaterSenseMeasurement[]
}

export const WatersenseView = ({ device, measurements }: WatersenseViewProps) => {
  const units = useUserUnits()

  // Fetch device parameters for reference lines
  const { data: deviceParams } = useQuery({
    queryKey: ['device-parameters', device.id],
    queryFn: () => deviceService.getDeviceParameters(device.id),
    retry: false // Don't retry if parameters don't exist
  })

  const latestMeasurement = measurements[0]

  // Helper to safely convert to number
  const toNum = (value: any): number | undefined => {
    if (value === null || value === undefined) return undefined
    const num = typeof value === 'number' ? value : Number(value)
    return isNaN(num) ? undefined : num
  }

  const chartData = useMemo(() => {
    const data = measurements
      .slice()
      .reverse()
      .map((m) => ({
        timestamp: formatChartTimestamp(m.measurementDateUtc),
        level: m.liquidLevel || 0,
        battery: m.batteryPercentage || 0,
        temperature: convertTemp(toNum(m.temperature) || 0, units.temperature),
        humidity: toNum(m.humidity) || 0,
        rainfall: toNum(m.rain) || 0,
      }))
    return data
  }, [measurements, units.temperature])

  // Calculate temperature domain
  const tempDomain = useMemo(() => {
    const temps = chartData
      .map(d => d.temperature)
      .filter(t => t > 0)

    if (temps.length === 0) return [convertTemp(0, units.temperature), convertTemp(40, units.temperature)]

    const min = Math.min(...temps)
    const max = Math.max(...temps)
    const range = max - min
    const padding = Math.max(range * 0.2, 5)

    return [Math.floor(min - padding), Math.ceil(max + padding)] as [number, number]
  }, [chartData])

  const stats = useMemo(() => {
    if (measurements.length === 0) return null

    const levels = measurements.map(m => m.liquidLevel || 0).filter(l => l > 0)
    if (levels.length === 0) return null

    const avg = levels.reduce((a, b) => a + b, 0) / levels.length
    const max = Math.max(...levels)
    const min = Math.min(...levels)

    // Calculate trend over 12 hours instead of just last two readings
    let trend = 0
    if (measurements.length > 1 && measurements[0].liquidLevel) {
      const now = parseISO(measurements[0].measurementDateUtc)
      const twelveHoursAgo = new Date(now.getTime() - (12 * 60 * 60 * 1000))

      // Find the measurement closest to 12 hours ago
      let closestMeasurement = measurements[1]
      let closestDiff = Math.abs(parseISO(measurements[1].measurementDateUtc).getTime() - twelveHoursAgo.getTime())

      for (let i = 2; i < measurements.length; i++) {
        const measurementTime = parseISO(measurements[i].measurementDateUtc)
        const diff = Math.abs(measurementTime.getTime() - twelveHoursAgo.getTime())
        if (diff < closestDiff) {
          closestDiff = diff
          closestMeasurement = measurements[i]
        }
        // Stop searching if we've gone past 12 hours ago
        if (measurementTime < twelveHoursAgo) break
      }

      if (closestMeasurement.liquidLevel) {
        trend = measurements[0].liquidLevel - closestMeasurement.liquidLevel
      }
    }

    return { avg, max, min, trend }
  }, [measurements])

  const getTrendIcon = (trend: number) => {
    if (trend > 5) return <TrendingUp className="h-4 w-4 text-green-500" />
    if (trend < -5) return <TrendingDown className="h-4 w-4 text-red-500" />
    return <Minus className="h-4 w-4 text-gray-500" />
  }

  // Calculate total rainfall for the period
  const totalRainfall = useMemo(() => {
    return measurements.reduce((sum, m) => {
      const rain = toNum(m.rain)
      return sum + (rain || 0)
    }, 0)
  }, [measurements])

  return (
    <div className="space-y-6">
      {/* Current Status Cards */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Water Level</CardTitle>
            <Droplet className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {latestMeasurement?.liquidLevel ? `${latestMeasurement.liquidLevel}mm` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {latestMeasurement?.measurementDateUtc ? formatUTCToLocal(latestMeasurement.measurementDateUtc, 'MMM d, HH:mm') : ''}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Trend</CardTitle>
            {stats && getTrendIcon(stats.trend)}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? `${stats.trend > 0 ? '+' : ''}${stats.trend.toFixed(0)}mm` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              Over last 12 hours
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average</CardTitle>
            <Badge variant="outline">AVG</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? `${stats.avg.toFixed(0)}mm` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              Min: {stats?.min}mm / Max: {stats?.max}mm
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Battery</CardTitle>
            <Battery className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {latestMeasurement?.batteryPercentage ? `${latestMeasurement.batteryPercentage}%` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              Solar: {((latestMeasurement?.solarVoltage || 0) / 1000).toFixed(2)}V
            </p>
          </CardContent>
        </Card>

        {/* Rainfall Card - Show if device has rainfall capability */}
        {chartData.some(d => d.rainfall !== undefined) && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Rainfall</CardTitle>
              <CloudRain className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {totalRainfall.toFixed(1)} mm
              </div>
              <p className="text-xs text-muted-foreground">
                Total for period
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Water Level Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Water Level History</CardTitle>
          <CardDescription>
            Liquid level measurements in millimeters over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={450}>
              <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 80 }}>
                <defs>
                  <linearGradient id="colorLevel" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.2}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Level (mm)', angle: -90, position: 'insideLeft' }}
                  width={60}
                  tickCount={8}
                />
                <Tooltip
                  formatter={(value: number) => [`${value.toFixed(0)}mm`, 'Level']}
                />
                <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: '20px' }} />
                <Area
                  type="monotone"
                  dataKey="level"
                  stroke="#3b82f6"
                  fill="url(#colorLevel)"
                  name="Water Level"
                  strokeWidth={2}
                />
                {/* Reference lines for full and empty levels */}
                {deviceParams?.valueFull && (
                  <ReferenceLine
                    y={deviceParams.valueFull}
                    stroke="#10b981"
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    label={{
                      value: `Full: ${deviceParams.valueFull}mm`,
                      position: 'right',
                      fill: '#10b981',
                      fontSize: 12
                    }}
                  />
                )}
                {deviceParams?.valueEmpty && deviceParams.valueEmpty > 0 && (
                  <ReferenceLine
                    y={deviceParams.valueEmpty}
                    stroke="#ef4444"
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    label={{
                      value: `Empty: ${deviceParams.valueEmpty}mm`,
                      position: 'right',
                      fill: '#ef4444',
                      fontSize: 12
                    }}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[450px] text-muted-foreground">
              No measurement data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rainfall Chart */}
      {chartData.some(d => d.rainfall !== undefined) && (
        <Card>
          <CardHeader>
            <CardTitle>Rainfall</CardTitle>
            <CardDescription>
              Precipitation measurements over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 45 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Rainfall (mm)', angle: -90, position: 'insideLeft' }}
                  width={60}
                  tickCount={8}
                />
                <Tooltip
                  formatter={(value: any) => {
                    const num = toNum(value)
                    return num !== undefined ? [`${num.toFixed(1)} mm`, 'Rainfall'] : ['N/A', 'Rainfall']
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="rainfall"
                  stroke="#2563eb"
                  name="Rainfall"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Temperature and Humidity Chart */}
      {chartData.some(d => d.temperature > 0 || d.humidity > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>Temperature & Humidity</CardTitle>
            <CardDescription>
              Environmental conditions over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 20, right: 80, left: 80, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  yAxisId="temp"
                  style={{ fontSize: '12px' }}
                  label={{ value: `Temperature (${tempLabel(units.temperature)})`, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                  domain={tempDomain}
                  width={60}
                  tickCount={8}
                />
                <YAxis
                  yAxisId="humidity"
                  orientation="right"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Humidity (%)', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                  domain={[0, 100]}
                  width={60}
                  tickCount={6}
                />
                <Tooltip />
                <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: '20px' }} />
                <Line
                  yAxisId="temp"
                  type="monotone"
                  dataKey="temperature"
                  stroke="#f59e0b"
                  name={`Temperature (${tempLabel(units.temperature)})`}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="humidity"
                  type="monotone"
                  dataKey="humidity"
                  stroke="#06b6d4"
                  name="Humidity (%)"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Battery Chart */}
      {chartData.some(d => d.battery > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>Battery Level</CardTitle>
            <CardDescription>
              Device battery percentage over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 45 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Battery (%)', angle: -90, position: 'insideLeft' }}
                  domain={[0, 100]}
                  width={60}
                  tickCount={6}
                />
                <Tooltip
                  formatter={(value: number) => [`${value}%`, 'Battery']}
                />
                <Line
                  type="monotone"
                  dataKey="battery"
                  stroke="#10b981"
                  name="Battery"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
